import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Layers, Search, RefreshCw, Calculator, FileCheck, AlertTriangle, Info, Send, Check, XCircle, ClipboardList, User, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import * as greigeStorageApi from '@/services/greigeStorageApi';

const StatusBadge = ({ status }) => {
  const map = {
    pending_approval: 'bg-yellow-100 text-yellow-700',
    generated: 'bg-blue-100 text-blue-700',
    assigned: 'bg-teal-100 text-teal-700',
    in_progress: 'bg-orange-100 text-orange-700',
    completed: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };
  return <Badge className={`${map[status] || 'bg-gray-100 text-gray-700'} border-0`} data-testid={`slip-status-${status}`}>{(status || '').replace(/_/g, ' ')}</Badge>;
};

// Conversion state of a completed slip: nothing picked / converted / pending.
const ConvState = ({ s }) => {
  const picked = (s.picked_scans?.length || 0) > 0 || (s.lines || []).some((l) => l.consumed_bundles?.length);
  const [cls, label] = !picked
    ? ['bg-gray-100 text-gray-700', 'Nothing picked — not converted']
    : s.converted
      ? ['bg-green-100 text-green-700', `Converted${s.conversion_count ? ` (${s.conversion_count} bundles)` : ''}`]
      : ['bg-amber-100 text-amber-800', 'Not converted — use Convert in Assortment Picking'];
  return <span className={`text-[11px] font-medium rounded px-1.5 py-0.5 ${cls}`} data-testid={`aps-slip-conv-state-${s.pick_slip_id}`}>{label}</span>;
};


const AssortmentPickSlip = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('create');

  const isAdmin = (() => {
    try {
      const role = (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase();
      return ['admin', 'site admin', 'super_admin'].includes(role);
    } catch { return false; }
  })();

  // ---- create state ----
  const [designs, setDesigns] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingDesigns, setLoadingDesigns] = useState(true);
  const [selected, setSelected] = useState(null);
  const [targetQty, setTargetQty] = useState('');
  const [preview, setPreview] = useState(null);
  const [splits, setSplits] = useState({});
  const [computing, setComputing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pool, setPool] = useState(null); // 'non_nq' | 'nq' — chosen pool for the split
  // ---- target design conversion ----
  const [targetOptions, setTargetOptions] = useState([]); // [{design_name, shade}]
  const [targetDesign, setTargetDesign] = useState(''); // '' = same design (no conversion)
  const [targetShade, setTargetShade] = useState('');
  const [loadingTargets, setLoadingTargets] = useState(false);

  // ---- approvals/slips state ----
  const [slips, setSlips] = useState([]);
  const [loadingSlips, setLoadingSlips] = useState(false);
  const [caps, setCaps] = useState({ canCreate: false, canApprove: false });
  const [acting, setActing] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, slipId: null, reason: '' });

  const fetchDesigns = async () => {
    setLoadingDesigns(true);
    try {
      const d = await greigeStorageApi.getAssortmentDesigns();
      setDesigns(d.designs || []);
    } catch {
      toast.error('Failed to load designs');
    } finally {
      setLoadingDesigns(false);
    }
  };

  const fetchSlips = useCallback(async () => {
    setLoadingSlips(true);
    try {
      const d = await greigeStorageApi.getAssortmentPickSlips();
      setSlips(d.pick_slips || []);
      if (typeof d.can_create === 'boolean') setCaps({ canCreate: d.can_create, canApprove: d.can_approve });
    } catch {
      toast.error('Failed to load pick slips');
    } finally {
      setLoadingSlips(false);
    }
  }, []);

  useEffect(() => { fetchDesigns(); fetchSlips(); }, [fetchSlips]);

  const filtered = designs.filter((d) =>
    !search || (d.design_name || '').toLowerCase().includes(search.toLowerCase()));

  const handleSelect = async (d) => {
    setSelected(d);
    setPreview(null);
    setSplits({});
    setTargetQty('');
    setPool(null);
    setTargetDesign('');
    setTargetShade('');
    setTargetOptions([]);
    setLoadingTargets(true);
    try {
      const res = await greigeStorageApi.getAssortmentTargetDesigns(d.design_name);
      setTargetOptions(res.target_options || []);
    } catch {
      setTargetOptions([]);
    } finally {
      setLoadingTargets(false);
    }
  };

  const runPreview = async (qty, poolArg) => {
    setComputing(true);
    try {
      const res = await greigeStorageApi.previewAssortment(selected.design_name, qty, poolArg);
      setPreview(res);
      setPool(res.pool);
      const s = {};
      (res.colors || []).forEach((c) => { s[c.color_name] = c.suggested_qty; });
      setSplits(s);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to compute split');
    } finally {
      setComputing(false);
    }
  };

  const handleCompute = () => {
    const qty = parseInt(targetQty, 10);
    if (!qty || qty <= 0) { toast.error('Enter a valid target quantity'); return; }
    runPreview(qty, pool);
  };

  const handleTogglePool = (newPool) => {
    if (computing || newPool === pool) return;
    const qty = parseInt(targetQty, 10);
    if (!qty || qty <= 0) return;
    runPreview(qty, newPool);
  };

  const canEdit = preview?.can_edit;
  const setColorQty = (color, val, max) => {
    let n = parseInt((val || '').toString().replace(/[^0-9]/g, ''), 10);
    if (isNaN(n)) n = 0;
    if (n > max) n = max;
    setSplits((prev) => ({ ...prev, [color]: n }));
  };

  const splitTotal = Object.values(splits).reduce((a, b) => a + (parseInt(b, 10) || 0), 0);
  const colorsInSplit = Object.values(splits).filter((q) => (parseInt(q, 10) || 0) > 0).length;
  const u = ((selected?.qty_unit || preview?.qty_unit) === 'Mtr') ? 'Mtr' : 'pcs';
  const lowColors = !!(preview?.need_assortment && colorsInSplit < 6);

  const handleSubmit = async () => {
    const color_splits = Object.entries(splits)
      .map(([color_name, qty]) => ({ color_name, qty: parseInt(qty, 10) || 0 }))
      .filter((x) => x.qty > 0);
    if (color_splits.length === 0) { toast.error('Set at least one colour quantity'); return; }
    setSubmitting(true);
    try {
      await greigeStorageApi.generateAssortment({
        design_name: selected.design_name,
        target_qty: parseInt(targetQty, 10),
        pool: preview.pool,
        color_splits,
        target_design_name: targetDesign || undefined,
        target_shade: targetShade || undefined,
      });
      toast.success('Pick slip submitted for approval');
      setSelected(null); setPreview(null); setSplits({}); setTargetQty(''); setPool(null);
      setTargetDesign(''); setTargetShade(''); setTargetOptions([]);
      await fetchSlips();
      setTab('approvals');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit pick slip');
    } finally {
      setSubmitting(false);
    }
  };

  const approveSlip = async (slipId) => {
    setActing(true);
    try {
      const res = await greigeStorageApi.approveAssortmentSlip(slipId);
      toast.success(res.message || 'Approved');
      await fetchSlips();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally { setActing(false); }
  };

  const confirmReject = async () => {
    setActing(true);
    try {
      await greigeStorageApi.rejectAssortmentSlip(rejectDialog.slipId, rejectDialog.reason);
      toast.success('Pick slip rejected');
      setRejectDialog({ open: false, slipId: null, reason: '' });
      await fetchSlips();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    } finally { setActing(false); }
  };

  const pendingCount = slips.filter((s) => s.status === 'pending_approval').length;

  const handleDeleteSlip = async (s) => {
    const msg = `Cancel & delete assortment pick slip "${s.design_name}" (${s.allocated_qty} pcs)?\n\n`
      + `Any bundles picked under this slip will be restored to stock. This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setActing(true);
    try {
      const res = await greigeStorageApi.deleteAssortmentSlip(s.pick_slip_id);
      toast.success(`Slip deleted — ${res.bundles_restored || 0} bundle(s) restored to stock`);
      await fetchSlips();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete slip');
    } finally { setActing(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 min-h-16 pt-12 pb-3 sm:pt-0 sm:pb-0 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/greige-storage')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="assortment-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Layers className="w-6 h-6" />
              <div>
                <h1 className="text-xl font-bold font-display">Assortment Pick Slip</h1>
                <p className="text-sm text-[#E8DCC8]">Build a colour split → approve → pick</p>
              </div>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/sections/greige-storage/assortment-picking')} className="bg-white/10 text-white border-white/20 hover:bg-white/20" data-testid="go-to-picking-btn">
            <FileCheck className="w-4 h-4 mr-2" /> Go to Picking
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="create" data-testid="aps-tab-create">Create Slip</TabsTrigger>
            <TabsTrigger value="approvals" data-testid="aps-tab-approvals">
              {caps.canApprove ? 'Approvals & Slips' : 'My Slips'}
              {pendingCount > 0 && <span className="ml-2 bg-yellow-500 text-white text-xs rounded-full px-1.5">{pendingCount}</span>}
            </TabsTrigger>
          </TabsList>

          {/* ---------------- CREATE ---------------- */}
          <TabsContent value="create">
            <div className="grid md:grid-cols-5 gap-6">
              {/* Design picker */}
              <div className="md:col-span-2 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-[#1A1F2E]">1 · Select Design</h2>
                  <Button variant="ghost" size="sm" onClick={fetchDesigns}><RefreshCw className={`w-4 h-4 ${loadingDesigns ? 'animate-spin' : ''}`} /></Button>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search design..." className="pl-10" data-testid="assortment-design-search" />
                </div>
                <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1" data-testid="assortment-design-list">
                  {loadingDesigns ? (
                    <div className="py-8 text-center text-[#5A5F6E]">Loading…</div>
                  ) : filtered.length === 0 ? (
                    <div className="py-8 text-center text-[#5A5F6E]">No designs with stored stock</div>
                  ) : filtered.map((d) => (
                    <button
                      key={d.design_name}
                      onClick={() => handleSelect(d)}
                      data-testid={`assortment-design-${d.design_name}`}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${selected?.design_name === d.design_name ? 'border-[#2D5F4F] bg-[#2D5F4F]/5' : 'border-[#E5DFD0] bg-white hover:border-[#2D5F4F]/50'}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-[#1A1F2E]">{d.design_name}</span>
                        <span className="text-xs font-semibold text-[#2D5F4F]" data-testid={`assortment-design-qty-${d.design_name}`}>{d.stored_pieces} {d.qty_unit === 'Mtr' ? 'Mtr' : 'pcs'}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-[#5A5F6E]">{d.stored_bundles} bundles</span>
                        <Badge variant="outline" className="text-[10px]">{d.colors} {d.colors === 1 ? 'colour' : 'colours'}</Badge>
                        {d.assortment_status === 'Need Assortment' && <Badge className="bg-indigo-100 text-indigo-700 border-0 text-[10px]">Need Assortment</Badge>}
                        {d.assortment_status === 'Assortment not required' && <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px]">No Assortment</Badge>}
                        {d.has_nq && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">NQ</Badge>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Split builder */}
              <div className="md:col-span-3 space-y-4">
                {!selected ? (
                  <Card className="border-[#E5DFD0]"><CardContent className="py-16 text-center text-[#5A5F6E]">
                    <Layers className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    Select a design to build an assortment split
                  </CardContent></Card>
                ) : (
                  <>
                    <Card className="border-[#E5DFD0]">
                      <CardHeader className="pb-2"><CardTitle className="text-base">2 · Target Quantity — {selected.design_name}</CardTitle></CardHeader>
                      <CardContent>
                        <div className="flex gap-3">
                          <Input
                            value={targetQty}
                            onChange={(e) => setTargetQty(e.target.value.replace(/[^0-9]/g, ''))}
                            placeholder={selected.qty_unit === 'Mtr' ? 'Total metres needed' : 'Total pieces needed'}
                            inputMode="numeric"
                            className="flex-1"
                            data-testid="assortment-target-input"
                          />
                          <Button onClick={handleCompute} disabled={computing} className="bg-[#2D5F4F] hover:bg-[#1A1F2E]" data-testid="assortment-compute-btn">
                            <Calculator className={`w-4 h-4 mr-2 ${computing ? 'animate-spin' : ''}`} /> Compute Split
                          </Button>
                        </div>
                        <p className="text-xs text-[#5A5F6E] mt-2">Available: <b>{selected.stored_pieces}</b> {u} across <b>{selected.stored_bundles}</b> bundles / {selected.colors} colour(s)</p>
                      </CardContent>
                    </Card>

                    {/* Target design + shade conversion (parent/child sort nos) — visible right after selecting a design */}
                    {(() => {
                      const opts = targetOptions;
                      const hasAlt = opts.length > 0;
                      const selVal = targetDesign ? `${targetDesign}||${targetShade}` : '__same__';
                      return (
                        <Card className="border-[#E5DFD0]" data-testid="assortment-target-design-block">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2">
                              <ArrowRight className="w-4 h-4 text-[#2D5F4F]" /> Target Design &amp; Shade (optional conversion)
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            <Select
                              value={selVal}
                              onValueChange={(v) => {
                                if (v === '__same__') { setTargetDesign(''); setTargetShade(''); return; }
                                const [dn, sh] = v.split('||');
                                setTargetDesign(dn); setTargetShade(sh || '');
                              }}
                              disabled={loadingTargets || !hasAlt}
                            >
                              <SelectTrigger data-testid="assortment-target-design-select">
                                <SelectValue placeholder="Same design (no conversion)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__same__" data-testid="assortment-target-design-same">Same design (no conversion)</SelectItem>
                                {opts.map((o) => {
                                  const val = `${o.design_name}||${o.shade || ''}`;
                                  return (
                                    <SelectItem key={val} value={val} data-testid={`assortment-target-design-option-${o.design_name}-${o.shade || 'na'}`}>
                                      {o.design_name}{o.shade ? ` · ${o.shade}` : ''}
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                            {loadingTargets ? (
                              <p className="text-xs text-[#5A5F6E]" data-testid="assortment-target-design-note">Loading target designs…</p>
                            ) : targetDesign ? (
                              <p className="text-xs text-[#2D5F4F] flex items-center gap-1" data-testid="assortment-target-design-note">
                                On finish, the picked greige will be design-transferred <b>{selected.design_name}</b> <ArrowRight className="w-3 h-3" /> <b>{targetDesign}{targetShade ? ` · ${targetShade}` : ''}</b> (via the matching child sort number).
                              </p>
                            ) : hasAlt ? (
                              <p className="text-xs text-[#5A5F6E]" data-testid="assortment-target-design-note">Leave as "Same design" to pick without conversion, or choose a target design &amp; shade to convert on finish.</p>
                            ) : (
                              <p className="text-xs text-[#5A5F6E]" data-testid="assortment-target-design-note">No alternate target designs available for this design.</p>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })()}

                    {preview && (
                      <Card className="border-[#E5DFD0]" data-testid="assortment-preview-card">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center justify-between">
                            <span>3 · Colour Split</span>
                            <div className="flex items-center gap-2">
                              <div className="inline-flex rounded-lg border border-[#E5DFD0] overflow-hidden" data-testid="assortment-pool-toggle">
                                <button
                                  type="button"
                                  onClick={() => handleTogglePool('non_nq')}
                                  disabled={computing || preview.non_nq_available === 0}
                                  className={`px-2.5 py-1 text-xs font-medium transition-colors ${preview.pool === 'non_nq' ? 'bg-emerald-600 text-white' : 'bg-white text-[#5A5F6E] hover:bg-emerald-50'} disabled:opacity-40 disabled:cursor-not-allowed`}
                                  data-testid="assortment-pool-non_nq"
                                >
                                  Non-NQ ({preview.non_nq_available})
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleTogglePool('nq')}
                                  disabled={computing || preview.nq_available === 0}
                                  className={`px-2.5 py-1 text-xs font-medium border-l border-[#E5DFD0] transition-colors ${preview.pool === 'nq' ? 'bg-amber-500 text-white' : 'bg-white text-[#5A5F6E] hover:bg-amber-50'} disabled:opacity-40 disabled:cursor-not-allowed`}
                                  data-testid="assortment-pool-nq"
                                >
                                  NQ ({preview.nq_available})
                                </button>
                              </div>
                              {preview.need_assortment && <Badge className="bg-indigo-100 text-indigo-700 border-0" data-testid="assortment-need-badge">Need Assortment</Badge>}
                            </div>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex items-start gap-2 text-xs text-[#5A5F6E] bg-[#FFFBF5] rounded p-2 border border-[#E5DFD0]">
                            <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#2D5F4F]" />
                            <span>{preview.pool_reason}. Non-NQ available: <b>{preview.non_nq_available}</b> · NQ available: <b>{preview.nq_available}</b> {u}.</span>
                          </div>

                          {lowColors && (
                            <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded p-2 border border-amber-200" data-testid="assortment-min6-warning">
                              <AlertTriangle className="w-4 h-4" /> Only {colorsInSplit} colour(s) — fewer than the ideal 6. The approver will need to confirm this on approval.
                            </div>
                          )}
                          {preview.shortfall > 0 && (
                            <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 rounded p-2 border border-red-200" data-testid="assortment-shortfall-warning">
                              <AlertTriangle className="w-4 h-4" /> Short by {preview.shortfall} {u} — not enough stock for the full target.
                            </div>
                          )}

                          <div className="border rounded-lg overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-gray-50">
                                  <TableHead className="text-xs">Colour</TableHead>
                                  <TableHead className="text-xs text-right">Available</TableHead>
                                  <TableHead className="text-xs text-right">Pick Qty</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {preview.colors.map((c) => (
                                  <TableRow key={c.color_name} data-testid={`assortment-color-row-${c.color_name}`}>
                                    <TableCell className="font-medium">{c.color_name}</TableCell>
                                    <TableCell className="text-right text-[#5A5F6E]">{c.available_pieces}</TableCell>
                                    <TableCell className="text-right">
                                      <Input
                                        value={splits[c.color_name] ?? 0}
                                        onChange={(e) => setColorQty(c.color_name, e.target.value, c.available_pieces)}
                                        disabled={!canEdit}
                                        inputMode="numeric"
                                        className="w-20 h-8 text-right ml-auto"
                                        data-testid={`assortment-color-qty-${c.color_name}`}
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>

                          <div className="flex items-center justify-between pt-1">
                            <span className="text-sm text-[#5A5F6E]">
                              Total split: <b className="text-[#1A1F2E]" data-testid="assortment-split-total">{splitTotal}</b> / target {preview.target_qty} {u} · {colorsInSplit} colour(s)
                            </span>
                            {!canEdit && <span className="text-xs text-amber-700">View only — elevated role required to create</span>}
                          </div>

                          <Button
                            onClick={handleSubmit}
                            disabled={submitting || !canEdit || splitTotal <= 0}
                            className="w-full bg-[#2D5F4F] hover:bg-[#1A1F2E]"
                            data-testid="assortment-submit-btn"
                          >
                            <Send className={`w-4 h-4 mr-2 ${submitting ? 'animate-spin' : ''}`} />
                            Submit for Approval ({splitTotal} {u})
                          </Button>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ---------------- APPROVALS / SLIPS ---------------- */}
          <TabsContent value="approvals">
            <div className="flex justify-end mb-3">
              <Button variant="ghost" size="sm" onClick={fetchSlips} data-testid="aps-refresh-slips"><RefreshCw className={`w-4 h-4 ${loadingSlips ? 'animate-spin' : ''}`} /></Button>
            </div>
            {slips.length === 0 ? (
              <Card className="border-[#E5DFD0]"><CardContent className="py-12 text-center text-[#5A5F6E]" data-testid="aps-no-slips">
                <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-40" />
                No assortment pick slips yet
              </CardContent></Card>
            ) : (
              <div className="space-y-2">
                {slips.map((s) => (
                  <Card key={s.pick_slip_id} className="border-[#E5DFD0]" data-testid={`aps-slip-row-${s.pick_slip_id}`}>
                    <CardContent className="py-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#1A1F2E] flex items-center gap-1.5 flex-wrap">
                            {s.design_name}
                            {s.needs_conversion && ((s.target_design_name && s.target_design_name !== s.design_name) || s.target_shade) && (
                              <>
                                <span className="inline-flex items-center gap-0.5 text-[11px] font-medium bg-indigo-100 text-indigo-700 rounded px-1.5 py-0.5" data-testid={`aps-slip-target-${s.pick_slip_id}`}>
                                  <ArrowRight className="w-3 h-3" /> {s.target_design_name}{s.target_shade ? ` · ${s.target_shade}` : ''}
                                </span>
                                {s.status === 'completed' && <ConvState s={s} />}
                              </>
                            )}
                          </p>
                          <p className="text-xs text-[#5A5F6E]">
                            {s.allocated_qty} {s.qty_unit === 'Mtr' ? 'Mtr' : 'pcs'} · {s.color_targets?.length || 0} {(s.color_targets?.length || 0) === 1 ? 'colour' : 'colours'} · {s.pool === 'nq' ? 'NQ' : 'Non-NQ'}
                            {s.low_colors ? ' · <6 colours' : ''} · by {s.created_by_name || s.generated_by}
                          </p>
                        </div>
                        <StatusBadge status={s.status} />
                        {caps.canApprove && s.status === 'pending_approval' && (
                          <div className="flex gap-1">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8" disabled={acting} onClick={() => approveSlip(s.pick_slip_id)} data-testid={`aps-approve-${s.pick_slip_id}`}>
                              <Check className="w-3.5 h-3.5 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 h-8" disabled={acting} onClick={() => setRejectDialog({ open: true, slipId: s.pick_slip_id, reason: '' })} data-testid={`aps-reject-${s.pick_slip_id}`}>
                              <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                            </Button>
                          </div>
                        )}
                        {isAdmin && (
                          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-8" disabled={acting} onClick={() => handleDeleteSlip(s)} data-testid={`aps-delete-${s.pick_slip_id}`} title="Cancel & delete this slip (restores picked bundles)">
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Cancel
                          </Button>
                        )}
                      </div>

                      {/* colour breakup */}
                      <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`aps-slip-colours-${s.pick_slip_id}`}>
                        {(s.color_targets || []).map((c) => (
                          <span key={c.color_name} className="text-[11px] inline-flex items-center gap-1 bg-[#2D5F4F]/10 text-[#2D5F4F] rounded px-2 py-0.5">
                            {c.color_name}: <b>{c.target_qty}</b>
                          </span>
                        ))}
                      </div>

                      {s.status === 'generated' || s.status === 'assigned' || s.status === 'in_progress' ? (
                        <p className="text-xs text-teal-700 pt-2 flex items-center gap-1" data-testid={`aps-slip-picker-${s.pick_slip_id}`}>
                          <User className="w-3 h-3" /> {s.assigned_picker_name ? `Assigned to ${s.assigned_picker_name}` : 'Approved — unassigned (set today\'s pickers, or assign in Picking)'}
                        </p>
                      ) : null}
                      {s.status === 'rejected' && <p className="text-xs text-red-600 pt-2">Rejected{s.reject_reason ? `: ${s.reject_reason}` : ''}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={rejectDialog.open} onOpenChange={(v) => setRejectDialog((p) => ({ ...p, open: v }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Assortment Pick Slip</DialogTitle>
            <DialogDescription>Optionally add a reason. The slip will be marked rejected and won't go to picking.</DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Reason (optional)" value={rejectDialog.reason} onChange={(e) => setRejectDialog((p) => ({ ...p, reason: e.target.value }))} data-testid="aps-reject-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, slipId: null, reason: '' })} data-testid="aps-reject-cancel-btn">Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" disabled={acting} onClick={confirmReject} data-testid="aps-reject-confirm-btn">Reject Slip</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssortmentPickSlip;
